import type { IdlingSettings, IdlingVehicleDecorated } from './idling-types';
import { CURR_SYMBOLS } from './idling-types';
import { fmtCost, fmtHrs, unitLabel } from './idling-page';

// ── KPI Cards ─────────────────────────────────────────────────────────────

export function renderIdlingKPIs(s: IdlingSettings, perVehicle: IdlingVehicleDecorated[]): void {
  const sym           = CURR_SYMBOLS[s.currency] ?? '$';
  const ul            = unitLabel(s.unit);
  const totalIdleHrs  = perVehicle.reduce((a, v) => a + v.idleHrs, 0);
  const totalFuel     = perVehicle.reduce((a, v) => a + v.fuelVol, 0);
  const totalCost     = perVehicle.reduce((a, v) => a + v.cost, 0);
  const totalEvents   = perVehicle.reduce((a, v) => a + v.eventCount, 0);
  const avgIdleHrs    = totalIdleHrs / (perVehicle.length || 1);
  const worst         = [...perVehicle].sort((a, b) => b.cost - a.cost)[0];
  const costPerUnit   = totalFuel > 0 ? totalCost / totalFuel : 0;

  (document.getElementById('idlingKpiGrid') as HTMLElement).innerHTML = `
    <div class="kpi-card kpi-hi">
      <div class="kpi-val" style="color:var(--accent);">${fmtCost(totalCost, sym)}</div>
      <div class="kpi-lbl">Total Idle Cost</div>
      <div class="kpi-sub">${s.currency} · ${totalFuel.toFixed(1)} ${ul} wasted</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${fmtHrs(totalIdleHrs)}</div>
      <div class="kpi-lbl">Total Idle Time</div>
      <div class="kpi-sub">hh:mm:ss across fleet</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${totalFuel.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${ul}</div>
      <div class="kpi-lbl">Fuel Wasted</div>
      <div class="kpi-sub">${fmtCost(costPerUnit, sym)} / ${ul}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${totalEvents.toLocaleString()}</div>
      <div class="kpi-lbl">Idle Events</div>
      <div class="kpi-sub">matches Geotab exception count</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${fmtHrs(avgIdleHrs)}</div>
      <div class="kpi-lbl">Avg Idle / Vehicle</div>
      <div class="kpi-sub">${perVehicle.length} vehicles analyzed</div>
    </div>
    <div class="kpi-card" style="border-color:rgba(208,70,63,0.28);background:rgba(208,70,63,0.06);">
      <div class="kpi-val" style="font-size:0.85rem;color:var(--critical);white-space:normal;word-break:break-word;line-height:1.2;" title="${worst ? escHtml(worst.name) : ''}">
        ${worst ? escHtml(worst.name) : '—'}
      </div>
      <div class="kpi-lbl">Worst Offender</div>
      <div class="kpi-sub">${worst ? fmtCost(worst.cost, sym) + ' · ' + worst.eventCount + ' events' : '—'}</div>
    </div>`;
}

// ── Charts ────────────────────────────────────────────────────────────────

let _rafId: number | null = null;

export function renderIdlingCharts(s: IdlingSettings, perVehicle: IdlingVehicleDecorated[]): void {
  if (_rafId !== null) { cancelAnimationFrame(_rafId); _rafId = null; }
  _rafId = requestAnimationFrame(() => {
    _rafId = null;
    drawBarChart(s, perVehicle);
    renderGroupChart(perVehicle);
  });
}

function rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  if (typeof (ctx as CanvasRenderingContext2D & { roundRect?: (...args: unknown[]) => void }).roundRect === 'function') {
    ctx.beginPath();
    (ctx as CanvasRenderingContext2D & { roundRect: (x: number, y: number, w: number, h: number, r: number) => void }).roundRect(x, y, w, h, r);
  } else {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }
}

function drawBarChart(s: IdlingSettings, perVehicle: IdlingVehicleDecorated[]): void {
  const canvas    = document.getElementById('idlingBarChart') as HTMLCanvasElement;
  const container = canvas.parentElement!;
  if (container.clientWidth === 0) return;
  const W         = Math.max(200, container.clientWidth - 32);
  const top10     = [...perVehicle].sort((a, b) => b.cost - a.cost).slice(0, 10);
  if (!top10.length) { canvas.width = W; canvas.height = 60; return; }

  const isLight = document.documentElement.getAttribute('data-theme') === 'light';
  const ul    = unitLabel(s.unit);
  const sym   = CURR_SYMBOLS[s.currency] ?? '$';
  const barH  = 26, gap = 8, padL = 170, padR = 130, padT = 8, padB = 8;
  const H     = top10.length * (barH + gap) + padT + padB;
  canvas.width = W; canvas.height = H;

  const ctx    = canvas.getContext('2d')!;
  const maxVal = top10[0].cost;
  const chartW = W - padL - padR;

  top10.forEach((v, i) => {
    const y    = padT + i * (barH + gap);
    const barW = maxVal > 0 ? (v.cost / maxVal) * chartW : 0;
    const grad = ctx.createLinearGradient(padL, 0, padL + barW, 0);
    grad.addColorStop(0, isLight ? 'rgba(245,158,11,0.90)' : 'rgba(255,170,32,0.85)');
    grad.addColorStop(1, isLight ? 'rgba(234,88,12,0.65)'  : 'rgba(255,140,20,0.55)');
    ctx.fillStyle = grad;
    rrect(ctx, padL, y, Math.max(barW, 3), barH, 4);
    ctx.fill();

    ctx.fillStyle = isLight ? '#0F172A' : '#c8d8ec';
    ctx.font = '600 11px Syne,sans-serif';
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    const dn = v.name.length > 22 ? v.name.slice(0, 21) + '…' : v.name;
    ctx.fillText(dn, padL - 8, y + barH / 2);

    ctx.fillStyle = isLight ? '#b45309' : 'rgba(255,170,32,0.9)';
    ctx.font = '500 10px IBM Plex Mono,monospace';
    ctx.textAlign = 'left';
    ctx.fillText(fmtCost(v.cost, sym) + '  ' + v.fuelVol.toFixed(1) + ul, padL + barW + 6, y + barH / 2);
  });

  ctx.strokeStyle = isLight ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.06)';
  ctx.lineWidth   = 1;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, H - padB); ctx.stroke();
}

// ── Idle Time by Group — horizontal stacked bar + legend (replaces the donut) ──

const GROUP_COLORS_DARK  = ['#e3a64e','#6f9be0','#8fb37a','#d0463f','#a07fd0','#5fc2b0','#d8693f','#c9a24b','#8a9bd4','#5fb0a0'];
const GROUP_COLORS_LIGHT = ['#c07a1e','#3f6fb0','#5a8f4a','#b03030','#7c4fb0','#2f9080','#b85a28','#9c7a1e','#4a6fb0','#2f8070'];

function escHtml(str: string): string {
  return str.replace(/[&<>"]/g, (c) => (
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'
  ));
}

function renderGroupChart(perVehicle: IdlingVehicleDecorated[]): void {
  const barEl = document.getElementById('idlingGroupBar');
  const legEl = document.getElementById('idlingGroupLegend');
  const totEl = document.getElementById('idlingGroupTotal');
  if (!barEl || !legEl) return;

  const isLight = document.documentElement.getAttribute('data-theme') === 'light';
  const COLORS  = isLight ? GROUP_COLORS_LIGHT : GROUP_COLORS_DARK;

  // A vehicle tagged under more than one top-level group (e.g. both "Tennant" and "Tennant
  // Vehicles") adds its hours to EACH group's own bucket rather than a merged "A, B" bucket, so
  // every group's total is complete. That means bucket values can overlap — `total` here is the
  // true, non-inflated fleet total (used only for each group's "% of fleet" label), not a sum of
  // the buckets, which can add up to more than the fleet total when groups overlap.
  const byGroup: Record<string, number> = {};
  perVehicle.forEach((v) => {
    v.groupRollups.forEach((g) => { byGroup[g] = (byGroup[g] ?? 0) + v.idleHrs; });
  });
  const entries = Object.entries(byGroup).sort((a, b) => b[1] - a[1]);
  const total   = perVehicle.reduce((sum, v) => sum + v.idleHrs, 0);

  if (totEl) totEl.textContent = total > 0 ? fmtHrs(total) + ' total' : '';

  if (!entries.length || !total) {
    barEl.innerHTML = '';
    legEl.innerHTML = '<div class="group-empty">No idle time recorded for this period.</div>';
    return;
  }

  barEl.innerHTML = entries.map((e, i) => {
    const pct = (e[1] / total) * 100;
    return `<div class="group-seg" style="flex:${e[1]};background:${COLORS[i % COLORS.length]}" title="${escHtml(e[0])} · ${fmtHrs(e[1])} (${pct.toFixed(0)}%)"></div>`;
  }).join('');

  legEl.innerHTML = entries.map((e, i) => {
    const pct   = (e[1] / total) * 100;
    const color = COLORS[i % COLORS.length];
    return `<div class="group-legend-row">
      <div class="group-legend-name">
        <span class="group-legend-dot" style="background:${color}"></span>
        <span title="${escHtml(e[0])}">${escHtml(e[0])}</span>
      </div>
      <div class="group-legend-vals">
        <span class="group-legend-hrs">${fmtHrs(e[1])}</span>
        <span class="group-legend-pct">${pct.toFixed(0)}%</span>
      </div>
    </div>`;
  }).join('');
}
